/*
 * MATES TERMS GATE
 * Blocks checkout until the customer accepts the terms of service
 * (acquiring-bank requirement). See snippets/mates-terms-consent.liquid.
 *
 * The checkout controls stay clickable on purpose: a `disabled` button
 * swallows clicks, so the shopper would get no explanation for why nothing
 * happens. Instead the click is intercepted and the reason is announced.
 *
 * Three guards:
 *   1. click (capture phase) on the checkout button or the dynamic wallet
 *      buttons container (Shop Pay / PayPal / ...) is cancelled;
 *   2. the cart form's submit event is cancelled, covering keyboard
 *      submits and any control not matched above;
 *   3. the controls are marked aria-disabled and dimmed, so the blocked
 *      state is visible and announced before the click.
 */
if (!customElements.get('mates-terms-gate')) {
  class MatesTermsGate extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('.mates-terms__checkbox');
      this.errorEl = this.querySelector('.mates-terms__error');
      if (!this.checkbox) return;

      this.form = document.getElementById(this.dataset.form);
      // Scope to this gate's own cart drawer / cart section.
      this.scope = this.closest('cart-drawer, .cart__footer, .cart__blocks, form') || document;

      this.onToggle = this.onToggle.bind(this);
      this.onCaptureClick = this.onCaptureClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.checkbox.addEventListener('change', this.onToggle);
      this.scope.addEventListener('click', this.onCaptureClick, true);
      if (this.form) this.form.addEventListener('submit', this.onSubmit);

      this.sync();

      // The cart drawer replaces its markup after quantity changes; keep
      // the blocked state applied to the fresh nodes.
      this.observer = new MutationObserver(() => this.sync());
      if (this.scope !== document) {
        this.observer.observe(this.scope, { childList: true, subtree: true });
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.scope) this.scope.removeEventListener('click', this.onCaptureClick, true);
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
    }

    get accepted() {
      return !!(this.checkbox && this.checkbox.checked);
    }

    gatedElements() {
      return Array.from(this.scope.querySelectorAll('[name="checkout"], .additional-checkout-buttons'));
    }

    sync() {
      const blocked = !this.accepted;
      this.gatedElements().forEach((el) => {
        el.classList.toggle('mates-terms-blocked', blocked);
        if (blocked) {
          el.setAttribute('aria-disabled', 'true');
        } else {
          el.removeAttribute('aria-disabled');
        }
      });
    }

    onCaptureClick(event) {
      if (this.accepted) return;
      const target = event.target.closest('[name="checkout"], .additional-checkout-buttons');
      if (!target || !this.scope.contains(target)) return;
      event.preventDefault();
      event.stopPropagation();
      this.showError();
    }

    onSubmit(event) {
      if (this.accepted) return;
      event.preventDefault();
      this.showError();
    }

    onToggle() {
      if (this.accepted) this.hideError();
      this.sync();
    }

    showError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = this.dataset.errorMessage;
      this.errorEl.hidden = false;
      this.checkbox.setAttribute('aria-invalid', 'true');
      this.classList.add('mates-terms--error');
      this.checkbox.focus();
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      this.checkbox.removeAttribute('aria-invalid');
      this.classList.remove('mates-terms--error');
    }
  }

  customElements.define('mates-terms-gate', MatesTermsGate);
}
